import React, { createContext, useContext, useReducer, useEffect } from 'react';
import authService from '../services/authService';
import {
  AuthState,
  AuthAction,
  UserProfile,
  LoginCredentials,
  LoginResponse,
} from '../types/auth';

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserProfile | null;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: true,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      };
    case 'LOGIN_SUCCESS':
      return {
        isAuthenticated: true,
        user: action.payload,
        isLoading: false,
      };
    case 'LOGIN_FAILURE':
      return {
        isAuthenticated: false,
        user: null,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        isAuthenticated: false,
        user: null,
        isLoading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      dispatch({ type: 'LOGIN_START' });

      const response: LoginResponse = await authService.login(credentials);

      // Handle the actual API response format
      if (response.access_token && response.user_id) {
        // Store the access token in localStorage
        authService.setAuthData(response.access_token, {} as any);

        // Fetch the user profile using the token
        const user = await authService.getProfile();

        if (user) {
          dispatch({ type: 'LOGIN_SUCCESS', payload: user });
        } else {
          throw new Error('Failed to fetch user profile');
        }
      } else {
        throw new Error('Invalid login response');
      }
    } catch (error) {
      console.error('Login failed:', error);
      dispatch({ type: 'LOGIN_FAILURE', payload: 'Login failed' });
      throw error;
    }
  };

  const loginWithGoogle = async (idToken: string): Promise<void> => {
    try {
      dispatch({ type: 'LOGIN_START' });

      const response: LoginResponse =
        await authService.loginWithGoogle(idToken);

      // Handle the actual API response format
      if (response.access_token && response.user_id) {
        // Store the access token in localStorage
        authService.setAuthData(response.access_token, {} as any);

        // Fetch the user profile using the token
        const user = await authService.getProfile();

        if (user) {
          dispatch({ type: 'LOGIN_SUCCESS', payload: user });
        } else {
          throw new Error('Failed to fetch user profile');
        }
      } else {
        throw new Error('Invalid Google login response');
      }
    } catch (error) {
      console.error('Google login failed:', error);
      dispatch({ type: 'LOGIN_FAILURE', payload: 'Google login failed' });
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with logout even if API call fails
    } finally {
      // Always clear local state regardless of API response
      dispatch({ type: 'LOGOUT' });
    }
  };

  const clearError = (): void => {
    // No error state in new interface, but keeping for compatibility
  };

  const initializeAuth = async (): Promise<void> => {
    try {
      const isAuthenticated = authService.isAuthenticated();
      if (isAuthenticated) {
        const user = await authService.getProfile();
        if (user) {
          dispatch({ type: 'LOGIN_SUCCESS', payload: user });
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      await authService.logout(); // This will clear localStorage
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  useEffect(() => {
    initializeAuth();
  }, []);

  const value: AuthContextType = {
    isAuthenticated: state.isAuthenticated,
    user: state.user,
    isLoading: state.isLoading,
    login,
    loginWithGoogle,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
